"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTicket } from "@/lib/hooks/api/tickets";

interface ExpandableTicketDetailsProps {
  ticketId: string;
}

/**
 * Expandable ticket details component for mobile detail modals
 * Shows ticket information in a collapsible section
 */
export function ExpandableTicketDetails({ ticketId }: ExpandableTicketDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: ticket, isLoading } = useTicket(ticketId);

  if (!ticket) {
    return null;
  }

  return (
    <div className="space-y-2">
      <button type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
      >
        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {isExpanded ? "Hide" : "View"} Ticket Details
      </button>

      {isExpanded && (
        <div className="mt-2 p-3 bg-card-50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2 text-xs">
          {ticket.title && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Title:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{ticket.title}</p>
            </div>
          )}
          {ticket.status && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1 capitalize">{ticket.status.replace("_", " ")}</p>
            </div>
          )}
          {ticket.priority && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Priority:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1 capitalize">{ticket.priority}</p>
            </div>
          )}
          {ticket.estimated_hours && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Estimated Hours:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{ticket.estimated_hours}h</p>
            </div>
          )}
          {(ticket.actual_hours ?? 0) > 0 && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Actual Hours:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{ticket.actual_hours}h</p>
            </div>
          )}
          {ticket.due_date && (
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Due Date:</span>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{new Date(ticket.due_date).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
